import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import DisplayNotifications from '../display/DisplayNotifications';
import { useAuth } from '../../contexts/AuthContext';

const Header: React.FC = () => {
  const [isNavCollapsed, setIsNavCollapsed] = useState(true);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isNotesDropdownOpen, setIsNotesDropdownOpen] = useState(false);
  const [isDisplaysDropdownOpen, setIsDisplaysDropdownOpen] = useState(false);
  const [isAdminDropdownOpen, setIsAdminDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notesDropdownRef = useRef<HTMLLIElement>(null);
  const displaysDropdownRef = useRef<HTMLLIElement>(null);
  const adminDropdownRef = useRef<HTMLLIElement>(null);
  
  // Get authentication state from AuthContext
  const { currentUser, logout } = useAuth();
  const isAuthenticated = !!currentUser;
  const userRole = currentUser?.role || null;
  // Check if user has paid subscription tier for display management
  const userSubscriptionTier = currentUser?.subscriptionTier || 'free';
  const hasPaidSubscription = ['individual', 'group', 'organization', 'custom'].includes(userSubscriptionTier);
  
  // Toggle navigation collapse on mobile
  const handleNavCollapse = () => setIsNavCollapsed(!isNavCollapsed);
  
  // Toggle dropdowns
  const toggleUserDropdown = () => setIsUserDropdownOpen(!isUserDropdownOpen);
  const toggleNotesDropdown = () => setIsNotesDropdownOpen(!isNotesDropdownOpen);
  const toggleDisplaysDropdown = () => setIsDisplaysDropdownOpen(!isDisplaysDropdownOpen);
  const toggleAdminDropdown = () => setIsAdminDropdownOpen(!isAdminDropdownOpen);
  
  // Handle scroll to hide/show navigation header (ClearPath style)
  useEffect(() => {
    let lastScrollTop = 0;

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if (scrollTop > lastScrollTop && scrollTop > 200) {
        // Scrolling down - hide header
        setIsScrolled(true);
      } else {
        // Scrolling up - show header
        setIsScrolled(false);
      }

      lastScrollTop = scrollTop;
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Close user dropdown
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsUserDropdownOpen(false);
      }
      // Close notes dropdown
      if (notesDropdownRef.current && !notesDropdownRef.current.contains(event.target as Node)) {
        setIsNotesDropdownOpen(false);
      }
      // Close displays dropdown
      if (displaysDropdownRef.current && !displaysDropdownRef.current.contains(event.target as Node)) {
        setIsDisplaysDropdownOpen(false);
      }
      // Close admin dropdown
      if (adminDropdownRef.current && !adminDropdownRef.current.contains(event.target as Node)) {
        setIsAdminDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Handle scroll to donate section
  const handleScrollToDonate = () => {
    // If we're on the home page, just scroll to the donate section
    if (location.pathname === '/') {
      const donateSection = document.getElementById('donate');
      if (donateSection) {
        donateSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to home page with hash, then scroll
      navigate('/#donate');
      // Use setTimeout to allow the page to load before scrolling
      setTimeout(() => {
        const donateSection = document.getElementById('donate');
        if (donateSection) {
          donateSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
    // Close mobile nav if open
    setIsNavCollapsed(true);
  };

  // Real logout function using AuthContext
  const handleLogout = async () => {
    try {
      setIsUserDropdownOpen(false); // Close dropdown first
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <>
      {/* Skip to main content link for accessibility */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Header wrapper - both emergency banner and main nav hide together on scroll */}
      <div className={`header-wrapper ${isScrolled ? 'header-hidden' : ''}`}>
        {/* Emergency Crisis Banner - Fixed (ClearPath Template) */}
        <div className="emergency-banner">
          <div className="emergency-content">
            <div className="emergency-message">
              <span className="emergency-icon">!</span>
              <span>Crisis? You can talk to someone right now</span>
            </div>
            <div className="emergency-buttons">
              <a href="tel:988" className="btn btn-emergency-solid">988 Lifeline</a>
              <a href="sms:741741&body=HELLO" className="btn btn-emergency-outline">Text 741741</a>
            </div>
          </div>
        </div>

        <nav
          id="main-header"
          className="navbar navbar-expand-lg navbar-light bg-white shadow-sm main-header"
        >
        <div className="container">
          {/* Logo and brand */}
          <Link className="navbar-brand" to="/">
            <img
              src="/assets/images/StampOutSuicide.png"
              alt="StampOutSuicide.org"
              className="navbar-logo"
              width={512}
              height={312}
            />
          </Link>
          
          {/* Mobile toggle */}
          <button 
            className="navbar-toggler" 
            type="button" 
            onClick={handleNavCollapse}
            aria-controls="navbarContent" 
            aria-expanded={!isNavCollapsed ? true : false} 
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          
          {/* Navigation links */}
          <div className={`${isNavCollapsed ? 'collapse' : ''} navbar-collapse`} id="navbarContent">
            <ul className="navbar-nav me-auto mb-2 mb-lg-0">
              <li className="nav-item">
                <NavLink 
                  className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} 
                  to="/"
                >
                  Home
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink 
                  className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`}
                  to="/notes"
                >
                  Browse Notes
                </NavLink>
              </li>
              
              {/* Create a Note link for unauthenticated users */}
              {!isAuthenticated && (
                <li className="nav-item">
                  <NavLink
                    className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`}
                    to="/submit"
                  >
                    Create a Note
                  </NavLink>
                </li>
              )}

              {/* Donate button - available to all users */}
              <li className="nav-item">
                <button
                  className="nav-link donate-nav-link"
                  onClick={handleScrollToDonate}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#E07A5F',
                    fontWeight: 600
                  }}
                >
                  <i className="fas fa-heart me-1" style={{ fontSize: '0.9em' }}></i>
                  Donate
                </button>
              </li>
              
              {/* Authenticated-only links */}
              {isAuthenticated && (
                <>
                  <li className="nav-item dropdown" ref={notesDropdownRef}>
                    <button 
                      className="nav-link dropdown-toggle" 
                      type="button"
                      onClick={toggleNotesDropdown}
                      aria-expanded={isNotesDropdownOpen}
                    >
                      Notes
                    </button>
                    <ul className={`dropdown-menu ${isNotesDropdownOpen ? 'show' : ''}`}>
                      <li>
                        <Link 
                          className="dropdown-item" 
                          to="/my-notes"
                          onClick={() => setIsNotesDropdownOpen(false)}
                        >
                          My Notes
                        </Link>
                      </li>
                      <li>
                        <Link 
                          className="dropdown-item" 
                          to="/my-notes/new"
                          onClick={() => setIsNotesDropdownOpen(false)}
                        >
                          Create Note
                        </Link>
                      </li>
                    </ul>
                  </li>
                  
                  {/* Display management - only show if user has paid subscription */}
                  {hasPaidSubscription && (
                    <li className="nav-item dropdown" ref={displaysDropdownRef}>
                      <button 
                        className="nav-link dropdown-toggle" 
                        type="button"
                        onClick={toggleDisplaysDropdown}
                        aria-expanded={isDisplaysDropdownOpen}
                      >
                        Displays
                      </button>
                      <ul className={`dropdown-menu ${isDisplaysDropdownOpen ? 'show' : ''}`}>
                        <li>
                          <Link 
                            className="dropdown-item" 
                            to="/my-displays"
                            onClick={() => setIsDisplaysDropdownOpen(false)}
                          >
                            My Displays
                          </Link>
                        </li>
                      </ul>
                    </li>
                  )}
                  
                  {/* Role-specific navigation - only show if user has proper role */}
                  {(userRole === 'moderator' || userRole === 'admin') && (
                    <li className="nav-item">
                      <NavLink 
                        className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`}
                        to="/moderator/queue"
                      >
                        Moderation Queue
                      </NavLink>
                    </li>
                  )}
                  
                  {userRole === 'admin' && (
                    <li className="nav-item dropdown" ref={adminDropdownRef}>
                      <button 
                        className="nav-link dropdown-toggle" 
                        type="button"
                        onClick={toggleAdminDropdown}
                        aria-expanded={isAdminDropdownOpen}
                      >
                        Admin
                      </button>
                      <ul className={`dropdown-menu ${isAdminDropdownOpen ? 'show' : ''}`}>
                        <li>
                          <Link 
                            className="dropdown-item" 
                            to="/admin/dashboard"
                            onClick={() => setIsAdminDropdownOpen(false)}
                          >
                            Dashboard
                          </Link>
                        </li>
                        <li>
                          <Link 
                            className="dropdown-item" 
                            to="/admin/users"
                            onClick={() => setIsAdminDropdownOpen(false)}
                          >
                            Manage Users
                          </Link>
                        </li>
                        <li>
                          <Link
                            className="dropdown-item"
                            to="/admin/activity"
                            onClick={() => setIsAdminDropdownOpen(false)}
                          >
                            Activity Logs
                          </Link>
                        </li>
                        <li>
                          <Link
                            className="dropdown-item"
                            to="/admin/posts"
                            onClick={() => setIsAdminDropdownOpen(false)}
                          >
                            Posts
                          </Link>
                        </li>
                      </ul>
                    </li>
                  )}
                </>
              )}
            </ul>
            
            {/* Auth buttons */}
            <div className="d-flex align-items-center">
              {isAuthenticated && (
                <div className="me-3">
                  <DisplayNotifications />
                </div>
              )}
              
              {isAuthenticated ? (
                <div className="dropdown" ref={dropdownRef}>
                  <button 
                    className="btn btn-outline-primary dropdown-toggle d-flex align-items-center" 
                    type="button" 
                    id="userDropdown" 
                    onClick={toggleUserDropdown}
                    aria-expanded={isUserDropdownOpen}
                  >
                    {/* User profile picture */}
                    {currentUser?.googleProfilePictureUrl ? (
                      <img 
                        src={currentUser.googleProfilePictureUrl} 
                        alt="Profile"
                        className="rounded-circle me-2"
                        style={{ width: '28px', height: '28px' }}
                        onError={(e) => {
                          // If image fails to load, hide it
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      /* Fallback icon if no profile picture */
                      <i className="fas fa-user-circle me-2" style={{ fontSize: '20px' }}></i>
                    )}
                    {currentUser?.firstName ? `${currentUser.firstName} ${currentUser.lastName}` : 'My Account'}
                  </button>
                  <ul className={`dropdown-menu dropdown-menu-end ${isUserDropdownOpen ? 'show' : ''}`} aria-labelledby="userDropdown">
                    <li>
                      <Link 
                        className="dropdown-item" 
                        to="/profile"
                        onClick={() => setIsUserDropdownOpen(false)}
                      >
                        <i className="fas fa-user me-2"></i>
                        Profile
                      </Link>
                    </li>
                    <li>
                      <Link 
                        className="dropdown-item" 
                        to="/donations"
                        onClick={() => setIsUserDropdownOpen(false)}
                      >
                        <i className="fas fa-heart me-2"></i>
                        Donations
                      </Link>
                    </li>
                    <li><hr className="dropdown-divider" /></li>
                    <li>
                      <button 
                        className="dropdown-item" 
                        onClick={handleLogout}
                      >
                        <i className="fas fa-sign-out-alt me-2"></i>
                        Sign Out
                      </button>
                    </li>
                  </ul>
                </div>
              ) : (
                <div>
                  <Link 
                    to="/login" 
                    className="btn btn-outline-primary me-2"
                  >
                    Login
                  </Link>
                  <Link 
                    to="/register" 
                    className="btn btn-primary"
                  >
                    Sign Up
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
        </nav>
      </div>
    </>
  );
};

export default Header; 